// src/components/EmailVerification.js - Email verification component
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import Button from './Button';
import Icon from './Icon';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function EmailVerification() {
  const location = useLocation();
  const navigate = useNavigate();
  const [token, setToken] = useState('');
  const [loading, setLoading] = useState(true);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const tokenParam = urlParams.get('token');
    
    if (tokenParam) {
      setToken(tokenParam);
      verifyEmail(tokenParam);
    } else {
      setError('Ingen verifieringslänk hittades');
      setLoading(false);
    }
  }, [location]);

  const verifyEmail = async (tokenToVerify) => {
    try {
      const response = await axios.post(`${API_BASE}/auth/email-verification/verify`, {
        token: tokenToVerify
      });

      if (response.data.verified) {
        setVerified(true);
        console.log('✅ Email verified successfully');
        
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login', { 
            state: { message: 'E-post verifierad! Du kan nu logga in på ditt konto.' }
          });
        }, 3000);
      } else {
        setError(response.data.message);
      }
    } catch (err) {
      console.error('❌ Email verification error:', err);
      setError(err.response?.data?.detail || 'Ett fel uppstod vid verifiering');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-lg mx-auto my-8 p-8 border border-gray-300 rounded-lg bg-white shadow-lg">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Verifierar din e-post...</p>
        </div>
      </div>
    );
  }

  if (verified) {
    return (
      <div className="max-w-lg mx-auto my-8 p-8 border border-gray-300 rounded-lg bg-white shadow-lg">
        <div className="text-center">
          <Icon name="CheckCircle" className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">E-post verifierad!</h2>
          <p className="text-gray-600 mb-6">
            Din e-postadress har verifierats framgångsrikt. Du omdirigeras till inloggningssidan...
          </p>
          
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <p className="text-green-800 text-sm">
              ✅ Ditt konto är nu aktivt och redo att användas.
            </p>
          </div>

          <Button 
            onClick={() => navigate('/login')}
            variant="primary"
            className="w-full"
          >
            Fortsätt till inloggning
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto my-8 p-8 border border-gray-300 rounded-lg bg-white shadow-lg">
      <div className="text-center">
        <Icon name="XCircle" className="w-16 h-16 text-red-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Verifiering misslyckades</h2>
        <p className="text-gray-600 mb-6">{error}</p>
        
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <p className="text-yellow-800 text-sm mb-2">
            <strong>Möjliga orsaker:</strong>
          </p>
          <ul className="text-yellow-700 text-sm text-left space-y-1">
            <li>• Länken har gått ut (giltiga i 24 timmar)</li>
            <li>• Länken har redan använts</li>
            <li>• E-posten är redan verifierad</li>
          </ul>
        </div>
        
        <div className="space-y-3">
          <Button 
            onClick={() => navigate('/login')}
            variant="primary"
            className="w-full"
          >
            Gå till inloggning
          </Button>
          
          <button 
            onClick={() => navigate('/resend-verification')}
            className="w-full text-sm text-blue-600 hover:text-blue-700 underline"
          >
            Skicka nytt verifieringsmeddelande
          </button>
        </div>
      </div>
    </div>
  );
}

export default EmailVerification;
